import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  ToggleButton, ToggleButtonGroup, Stack, Typography, MenuItem, Box,
} from '@mui/material';
import { useChecklistQuery, useProvestKontrolaMutation, useUploadFotoMutation } from '../../api/JsdhNotifikaceService/service/HydrantBodMutations';
import { HYDRANT_CHECKLIST } from '../../Pages/Admin/hydranty/hydrantChecklist';
import {
  ChecklistHodnota, ChecklistVysledek, CreateHydrantKontrolaRequest, KontrolaStav,
} from '../../api/JsdhNotifikaceService/generated/models/HydrantBod';

interface Props { open: boolean; hydrantId: number; onClose: () => void; }

const HydrantKontrolaDialog: React.FC<Props> = ({ open, hydrantId, onClose }) => {
  const { data: checklist } = useChecklistQuery(open ? hydrantId : undefined);
  const provest = useProvestKontrolaMutation();
  const uploadFoto = useUploadFotoMutation();
  const polozky = checklist ?? HYDRANT_CHECKLIST;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [vysledky, setVysledky] = useState<Record<string, ChecklistHodnota>>({});
  const [prutok, setPrutok] = useState('');
  const [statickyTlak, setStatickyTlak] = useState('');
  const [tlak, setTlak] = useState('');
  const [stav, setStav] = useState<KontrolaStav>('OK');
  const [poznamka, setPoznamka] = useState('');
  const [kontroloval, setKontroloval] = useState('');
  const [zavadaPopis, setZavadaPopis] = useState('');
  const [fotoFiles, setFotoFiles] = useState<File[]>([]);

  useEffect(() => {
    if (open) {
      setVysledky({}); setPrutok(''); setStatickyTlak(''); setTlak(''); setStav('OK');
      setPoznamka(''); setKontroloval(''); setZavadaPopis(''); setFotoFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  const setPol = (kod: string, val: ChecklistHodnota) =>
    setVysledky((v) => ({ ...v, [kod]: val }));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFotoFiles(Array.from(e.target.files));
    }
  };

  const isPending = provest.isPending || uploadFoto.isPending;

  const ulozit = async () => {
    const checklistArr: ChecklistVysledek[] = polozky
      .filter((p) => !p.mereny)
      .map((p) => ({ kod: p.kod, hodnota: vysledky[p.kod] ?? 'OK' }));
    const data: CreateHydrantKontrolaRequest = {
      checklist: checklistArr,
      prutokLs: prutok ? Number(prutok) : undefined,
      statickyTlakBar: statickyTlak ? Number(statickyTlak) : undefined,
      tlakBar: tlak ? Number(tlak) : undefined,
      stav, poznamka, kontrolovalJmeno: kontroloval || undefined,
      zavadaPopis: stav !== 'OK' ? zavadaPopis : undefined,
      zavadaPriorita: stav === 'NEFUNKCNI' ? 'VYSOKA' : 'STREDNI',
    };

    try {
      const kontrola = await provest.mutateAsync({ id: hydrantId, data });
      for (const file of fotoFiles) {
        try {
          await uploadFoto.mutateAsync({ kontrolaId: kontrola.id, file });
        } catch {
          // upload chyba se zobrazí přes onError toast; kontrola je již uložena
        }
      }
      onClose();
    } catch {
      // chyba vytvoření kontroly se zobrazí přes onError toast
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Provést kontrolu hydrantu</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Kontroloval (jméno)" value={kontroloval} onChange={(e) => setKontroloval(e.target.value)} />
          {polozky.filter((p) => !p.mereny).map((p) => (
            <Stack key={p.kod} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{p.nazev}</Typography>
              <ToggleButtonGroup size="small" exclusive value={vysledky[p.kod] ?? 'OK'}
                onChange={(_, v) => v && setPol(p.kod, v)}>
                <ToggleButton value="OK">OK</ToggleButton>
                <ToggleButton value="ZAVADA">Závada</ToggleButton>
                <ToggleButton value="NELZE_OVERIT">N/A</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ))}
          <Stack direction="row" spacing={2}>
            <TextField label="Průtok (l/s)" type="number" value={prutok} onChange={(e) => setPrutok(e.target.value)} />
            <TextField label="Statický tlak (bar)" type="number" value={statickyTlak} onChange={(e) => setStatickyTlak(e.target.value)} />
            <TextField label="Hydrodynamický tlak (bar)" type="number" value={tlak} onChange={(e) => setTlak(e.target.value)} />
          </Stack>
          <TextField select label="Celkový stav" value={stav} onChange={(e) => setStav(e.target.value as KontrolaStav)}>
            <MenuItem value="OK">OK</MenuItem>
            <MenuItem value="ZAVADA">Závada</MenuItem>
            <MenuItem value="NEFUNKCNI">Nefunkční</MenuItem>
          </TextField>
          {stav !== 'OK' && (
            <TextField label="Popis závady" multiline value={zavadaPopis} onChange={(e) => setZavadaPopis(e.target.value)} />
          )}
          <TextField label="Poznámka" multiline value={poznamka} onChange={(e) => setPoznamka(e.target.value)} />
          <Box>
            <Button variant="outlined" size="small" component="label">
              Přiložit foto
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                multiple
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {fotoFiles.length > 0 && (
              <Typography variant="caption" sx={{ ml: 1 }}>
                {fotoFiles.map((f) => f.name).join(', ')}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Zrušit</Button>
        <Button variant="contained" disabled={isPending} onClick={ulozit}>Uložit kontrolu</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HydrantKontrolaDialog;
